import { useEffect, useState, useCallback, useRef, type JSX } from 'react'
import { useNavigate } from 'react-router'
import {
  Bell,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Calendar,
  X,
  RefreshCw,
  UserPlus,
  ArrowLeftRight,
  CircleCheck,
  ShieldCheck,
  MessageSquareText,
  FilePlus2,
  ChevronDown,
  Megaphone,
  Check,
  RotateCcw,
} from 'lucide-react'
import { api } from '../../api/client.js'
import {
  useNotificationsStore,
  type NotificationItem,
} from '../../store/notificationsStore.js'
import Tooltip from '../../components/ui/Tooltip.js'
import { formatInTimezone } from '../../utils/timezone.js'
import DatePicker from '../../components/ui/DatePicker.js'

const PAGE_SIZE = 10

type PageToken = { kind: 'page'; value: number } | { kind: 'ellipsis'; afterPage: number }

// Collapsed page-number sequence around the current page, e.g.
// 1 … 4 5 [6] 7 8 … 12 — never renders every page for large totals.
function buildPageSequence(current: number, totalPages: number): PageToken[] {
  const pages = new Set<number>([1, totalPages, current, current - 1, current + 1])
  const sorted = [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b)
  const result: PageToken[] = []
  let prev = 0
  for (const p of sorted) {
    if (prev && p - prev > 1) result.push({ kind: 'ellipsis', afterPage: prev })
    result.push({ kind: 'page', value: p })
    prev = p
  }
  return result
}

// Mirrors the exact eventKey literals notification-center-service.ts writes
// to the notifications table (see conversation-engine.ts, transfer-engine.ts,
// approval-engine.ts, ingestor.ts) — NOT a freeform namespace, so every value
// here must match one actually emitted, or the row falls through to the
// gray Bell fallback below.
const ACTIVITY_TYPES = [
  { label: 'All Activities', value: '', icon: Bell },
  { label: 'New Conversation', value: 'new_ticket', icon: FilePlus2 },
  { label: 'Conversation Created', value: 'ticket_created', icon: FilePlus2 },
  { label: 'Conversation Closed', value: 'ticket_closed', icon: CircleCheck },
  { label: 'Conversation Assigned', value: 'conversation_assigned', icon: UserPlus },
  { label: 'Conversation Transferred To You', value: 'conversation_transferred_to', icon: ArrowLeftRight },
  { label: 'Conversation Transferred From You', value: 'conversation_transferred_from', icon: ArrowLeftRight },
  { label: 'Client Replied', value: 'client_replied', icon: MessageSquareText },
  { label: 'Approval Requested', value: 'approval_requested', icon: ShieldCheck },
  { label: 'Conversation Created (Agent)', value: 'agent.ticket_created', icon: FilePlus2 },
  { label: 'Conversation Assigned (Agent)', value: 'agent.ticket_assigned', icon: UserPlus },
  { label: 'Conversation Assigned to Agent', value: 'agent.ticket_assigned_agent', icon: UserPlus },
  { label: 'Conversation Transferred (Agent)', value: 'agent.ticket_transferred', icon: ArrowLeftRight },
]

const EVENT_ICON_MAP: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
  new_ticket: { icon: FilePlus2, color: 'text-blue-600', bg: 'bg-blue-50' },
  ticket_created: { icon: FilePlus2, color: 'text-blue-600', bg: 'bg-blue-50' },
  'agent.ticket_created': { icon: FilePlus2, color: 'text-blue-600', bg: 'bg-blue-50' },
  ticket_closed: { icon: CircleCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  conversation_assigned: { icon: UserPlus, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  'agent.ticket_assigned': { icon: UserPlus, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  'agent.ticket_assigned_agent': { icon: UserPlus, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  conversation_transferred_to: { icon: ArrowLeftRight, color: 'text-purple-600', bg: 'bg-purple-50' },
  conversation_transferred_from: { icon: ArrowLeftRight, color: 'text-purple-600', bg: 'bg-purple-50' },
  'agent.ticket_transferred': { icon: ArrowLeftRight, color: 'text-purple-600', bg: 'bg-purple-50' },
  client_replied: { icon: MessageSquareText, color: 'text-sky-600', bg: 'bg-sky-50' },
  approval_requested: { icon: ShieldCheck, color: 'text-amber-600', bg: 'bg-amber-50' },
}

function getEventIcon(eventKey: string): { icon: typeof Bell; color: string; bg: string } {
  return EVENT_ICON_MAP[eventKey] ?? { icon: Bell, color: 'text-slate-500', bg: 'bg-slate-50' }
}

function getActivityLabel(eventKey: string): string {
  const exact = ACTIVITY_TYPES.find((a) => a.value === eventKey)
  if (exact) return exact.label
  return eventKey
    .split(/[._]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return formatInTimezone(d, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }, 'en-US')
}

interface CampaignOption {
  id: string
  name: string
}

/* ── Design tokens shared with the Inbox page, so both surfaces read as one
   system — see ConversationInbox.tsx for the canonical definitions. ── */
const R = 'rounded-[3px]'
const CONTROL = `flex h-8 items-center gap-2 ${R} border border-slate-200 bg-white px-2.5 text-[12.5px] font-semibold text-slate-600 transition-colors hover:border-slate-300 hover:text-slate-900`
const CONTROL_ON = 'border-[#C9D9FB] bg-blue-50 text-[#3366FF] hover:border-[#C9D9FB] hover:text-[#3366FF]'
const ICON_BTN = `flex h-9 w-9 shrink-0 items-center justify-center ${R} border border-slate-200 bg-white text-slate-500 transition-colors hover:border-slate-300 hover:text-slate-900`
const MENU = `absolute top-full z-40 mt-1.5 overflow-hidden ${R} border border-slate-200 bg-white py-1 shadow-xl shadow-black/5`
const MENU_ITEM = 'flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] font-medium transition-colors'

export default function NotificationsPage(): JSX.Element {
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const navigate = useNavigate()
  const [scope, setScope] = useState<'mine' | 'all'>('mine')

  // Input filter state (what the user types/selects)
  const [inputFromDate, setInputFromDate] = useState('')
  const [inputToDate, setInputToDate] = useState('')
  const [inputCampaign, setInputCampaign] = useState('')
  const [inputActivityType, setInputActivityType] = useState('')

  // Applied filter state (what's actually sent to the API)
  const [appliedFromDate, setAppliedFromDate] = useState('')
  const [appliedToDate, setAppliedToDate] = useState('')
  const [appliedCampaign, setAppliedCampaign] = useState('')
  const [appliedActivityType, setAppliedActivityType] = useState('')

  const isFiltered = !!(appliedFromDate || appliedToDate || appliedCampaign || appliedActivityType)

  // Campaign dropdown data
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([])

  // Pill-style filter menus (Inbox-style dropdown controls)
  const [campaignMenuOpen, setCampaignMenuOpen] = useState(false)
  const [activityMenuOpen, setActivityMenuOpen] = useState(false)
  const [showDateFilter, setShowDateFilter] = useState(false)
  const campaignMenuRef = useRef<HTMLDivElement>(null)
  const activityMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (campaignMenuRef.current && !campaignMenuRef.current.contains(e.target as Node)) setCampaignMenuOpen(false)
      if (activityMenuRef.current && !activityMenuRef.current.contains(e.target as Node)) setActivityMenuOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // The store is shared globally (also mutated by live socket pushes via
  // addLive, which always targets the viewer's own notifications). The All
  // tab must only reflect explicit fetches — per spec it never live-updates
  // — so it renders a page-local snapshot instead of the live store value,
  // preventing a live "mine" push from bleeding into the "all" list.
  const [allTabSnapshot, setAllTabSnapshot] = useState<NotificationItem[]>([])
  const storeNotifications = useNotificationsStore((s) => s.notifications)
  const notifications = scope === 'all' ? allTabSnapshot : storeNotifications
  const unreadCount = useNotificationsStore((s) => s.unreadCount)

  // Load campaigns for dropdown
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await api.get<{ campaigns: CampaignOption[]; teams: unknown[] }>('/agent/me/campaigns-teams')
        if (!cancelled && res.campaigns) setCampaigns(res.campaigns)
      } catch {
        // ignore — dropdown just won't populate
      }
    })()
    return () => { cancelled = true }
  }, [])

  const loadPage = useCallback(async () => {
    setLoading(true)
    try {
      const offset = (page - 1) * PAGE_SIZE
      const params = new URLSearchParams()
      params.set('offset', String(offset))
      params.set('limit', String(PAGE_SIZE))
      params.set('scope', scope)
      if (appliedFromDate) params.set('fromDate', appliedFromDate)
      if (appliedToDate) params.set('toDate', appliedToDate)
      if (appliedCampaign) params.set('campaign', appliedCampaign)
      if (appliedActivityType) params.set('eventKey', appliedActivityType)

      const [listRes, countRes] = await Promise.all([
        api.get<{ notifications: NotificationItem[]; hasMore: boolean; total: number }>(
          `/notifications?${params.toString()}`,
        ),
        api.get<{ count: number }>('/notifications/unread-count'),
      ])
      if (scope === 'all') {
        // Keep the shared store's own notifications list untouched — it's
        // "mine" data used by the bell widget and live-push elsewhere — but
        // still refresh unreadCount (always a "mine" count regardless of
        // scope) so the header badge stays correct even if this page is
        // opened directly on the All tab.
        setAllTabSnapshot(listRes.notifications ?? [])
        useNotificationsStore.getState().setInitial(
          useNotificationsStore.getState().notifications,
          countRes.count,
        )
      } else {
        useNotificationsStore.getState().setInitial(listRes.notifications ?? [], countRes.count)
      }
      setTotal(listRes.total ?? 0)
    } catch {
      // Keep whatever is already in the store on error.
    } finally {
      setLoading(false)
    }
  }, [page, scope, appliedFromDate, appliedToDate, appliedCampaign, appliedActivityType])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (cancelled) return
      await loadPage()
    })()
    return () => { cancelled = true }
  }, [loadPage])

  async function handleClick(n: NotificationItem): Promise<void> {
    if (scope === 'mine' && !n.isRead) {
      try {
        await api.patch(`/notifications/${n.id}/read`)
      } catch {
        // Optimistically mark read regardless; the badge polling recovers.
      }
      useNotificationsStore.getState().markRead(n.id)
    }
    if (n.conversationId) {
      // A still-staged Live Chat (live_chats.id, pre "Create Conversation")
      // shares the conversationId field with finalized conversations.id —
      // the inbox route only resolves the latter and 404s on the former.
      if (n.entityType === 'live_chat') {
        navigate(`/agent/chat?conversationId=${n.conversationId}&channel=chat`)
      } else {
        navigate(`/agent/conversation/inbox?scope=all&conversationId=${n.conversationId}`)
      }
    }
  }

  async function handleMarkAllRead(): Promise<void> {
    try {
      await api.post('/notifications/read-all')
    } catch {
      // Optimistically clear regardless.
    }
    useNotificationsStore.getState().markAllRead()
  }

  async function handleDelete(id: string): Promise<void> {
    try {
      await api.delete(`/notifications/${id}`)
      useNotificationsStore.getState().setNotifications(
        useNotificationsStore.getState().notifications.filter((n) => n.id !== id),
      )
      setTotal((t) => Math.max(0, t - 1))
    } catch {
      // ignore
    }
  }

  function handleClearFilter(): void {
    setInputFromDate('')
    setInputToDate('')
    setInputCampaign('')
    setInputActivityType('')
    setAppliedFromDate('')
    setAppliedToDate('')
    setAppliedCampaign('')
    setAppliedActivityType('')
    setPage(1)
  }

  // Campaign/Activity apply immediately on selection; date range applies as
  // soon as both From and To are set — no separate Apply step.
  function handleCampaignSelect(value: string): void {
    setInputCampaign(value)
    setAppliedCampaign(value)
    setPage(1)
    setCampaignMenuOpen(false)
  }

  function handleActivitySelect(value: string): void {
    setInputActivityType(value)
    setAppliedActivityType(value)
    setPage(1)
    setActivityMenuOpen(false)
  }

  function handleFromDateChange(value: string): void {
    setInputFromDate(value)
    if (value && inputToDate) {
      setAppliedFromDate(value)
      setAppliedToDate(inputToDate)
      setPage(1)
    }
  }

  function handleToDateChange(value: string): void {
    setInputToDate(value)
    if (inputFromDate && value) {
      setAppliedFromDate(inputFromDate)
      setAppliedToDate(value)
      setPage(1)
    }
  }

  function handleDateClear(): void {
    setInputFromDate('')
    setInputToDate('')
    setAppliedFromDate('')
    setAppliedToDate('')
    setShowDateFilter(false)
    setPage(1)
  }

  function handleTabChange(next: 'mine' | 'all'): void {
    setScope(next)
    setPage(1)
  }

  function handleRefresh(): void {
    void loadPage()
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const pageSequence = buildPageSequence(page, totalPages)

  const activeActivityMeta = ACTIVITY_TYPES.find((a) => a.value === inputActivityType) ?? ACTIVITY_TYPES[0]!
  const dateActive = Boolean(appliedFromDate || appliedToDate)

  return (
    <div className="flex h-full flex-col bg-[#F4F6F8]">
      {/* ── Header: identity · scope tabs · actions — mirrors ConversationInbox's header ── */}
      <header className="flex flex-wrap items-center gap-x-5 gap-y-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="flex shrink-0 items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center ${R} bg-blue-50 text-[#3366FF]`}>
            <Bell className="h-5 w-5" />
          </span>
          <div>
            <h1 className="text-[17px] font-bold leading-tight tracking-tight text-[#0f172a]">Notifications</h1>
            <p className="text-[12px] leading-tight text-slate-400">
              {unreadCount > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#3366FF]" />
                  {unreadCount} unread
                </span>
              ) : (
                'All caught up'
              )}
            </p>
          </div>
        </div>

        {/* Scope tabs — same segmented-control pattern as Inbox's audience scopes */}
        <div className={`flex shrink-0 items-center gap-1 ${R} bg-slate-100 p-1`}>
          <button
            type="button"
            onClick={() => handleTabChange('mine')}
            className={`flex items-center gap-2 ${R} px-3.5 py-1.5 text-[13px] font-semibold transition-all ${scope === 'mine' ? 'bg-white text-[#0f172a] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
          >
            My Notification
          </button>
          <button
            type="button"
            onClick={() => handleTabChange('all')}
            className={`flex items-center gap-2 ${R} px-3.5 py-1.5 text-[13px] font-semibold transition-all ${scope === 'all' ? 'bg-white text-[#0f172a] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
          >
            All Notification
          </button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Tooltip content="Refresh">
            <button type="button" onClick={handleRefresh} aria-label="Refresh" className={ICON_BTN}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </Tooltip>
          {scope === 'mine' && (
            <button
              type="button"
              onClick={() => void handleMarkAllRead()}
              disabled={unreadCount === 0}
              className={`flex h-9 shrink-0 items-center gap-1.5 ${R} bg-[#3366FF] px-4 text-[13px] font-semibold text-white transition-colors hover:bg-[#2952CC] disabled:cursor-not-allowed disabled:opacity-40`}
            >
              <CheckCheck className="h-4 w-4" />
              Mark all read
            </button>
          )}
        </div>
      </header>

      {/* ── Toolbar: pill-style filter controls — mirrors Inbox's toolbar row ── */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-200 bg-white px-5 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Campaign */}
          <div ref={campaignMenuRef} className="relative">
            <button type="button" onClick={() => setCampaignMenuOpen((v) => !v)} className={`${CONTROL} ${inputCampaign ? CONTROL_ON : ''}`}>
              <Megaphone className={`h-4 w-4 ${inputCampaign ? 'text-[#3366FF]' : 'text-slate-400'}`} />
              {inputCampaign || 'All Campaigns'}
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${campaignMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {campaignMenuOpen && (
              <div className={`${MENU} left-0 w-60`}>
                <button type="button" onClick={() => handleCampaignSelect('')} className={`${MENU_ITEM} ${inputCampaign === '' ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <Megaphone className="h-4 w-4 shrink-0" />
                  <span className="flex-1">All Campaigns</span>
                  {inputCampaign === '' && <Check className="h-4 w-4" />}
                </button>
                {campaigns.length > 0 && <div className="my-1 border-t border-slate-100" />}
                <div className="max-h-64 overflow-y-auto">
                  {campaigns.map((c) => {
                    const on = inputCampaign === c.name
                    return (
                      <button key={c.id} type="button" onClick={() => handleCampaignSelect(c.name)} className={`${MENU_ITEM} ${on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}>
                        <span className="flex-1 truncate">{c.name}</span>
                        {on && <Check className="h-4 w-4" />}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Activity */}
          <div ref={activityMenuRef} className="relative">
            <button type="button" onClick={() => setActivityMenuOpen((v) => !v)} className={`${CONTROL} ${inputActivityType ? CONTROL_ON : ''}`}>
              <activeActivityMeta.icon className={`h-4 w-4 ${inputActivityType ? 'text-[#3366FF]' : 'text-slate-400'}`} />
              {activeActivityMeta.label}
              <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${activityMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {activityMenuOpen && (
              <div className={`${MENU} left-0 w-64`}>
                {ACTIVITY_TYPES.map((opt) => {
                  const on = inputActivityType === opt.value
                  return (
                    <button key={opt.value} type="button" onClick={() => handleActivitySelect(opt.value)} className={`${MENU_ITEM} ${on ? 'bg-blue-50 text-[#3366FF]' : 'text-slate-600 hover:bg-slate-50'}`}>
                      <opt.icon className={`h-4 w-4 ${on ? 'text-[#3366FF]' : 'text-slate-400'}`} />
                      <span className="flex-1">{opt.label}</span>
                      {on && <Check className="h-4 w-4" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {/* Date range */}
          {showDateFilter || dateActive ? (
            <div className="flex items-center gap-1.5">
              <DatePicker
                ariaLabel="Notifications from date"
                size="sm"
                label="From"
                value={inputFromDate}
                onChange={(v) => handleFromDateChange(v)}
              />
              <DatePicker
                ariaLabel="Notifications to date"
                size="sm"
                label="To"
                value={inputToDate}
                onChange={(v) => handleToDateChange(v)}
              />
              <Tooltip content="Clear dates">
                <button type="button" onClick={handleDateClear} aria-label="Clear dates" className={`${R} p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </Tooltip>
            </div>
          ) : (
            <Tooltip content="Filter by date">
              <button type="button" onClick={() => setShowDateFilter(true)} className={CONTROL}>
                <Calendar className="h-4 w-4 text-slate-400" />Date
              </button>
            </Tooltip>
          )}

          {isFiltered && (
            <button
              type="button"
              onClick={handleClearFilter}
              className={`flex h-8 items-center gap-1.5 ${R} border border-slate-200 bg-white px-3 text-[12.5px] font-semibold text-slate-600 transition-colors hover:bg-slate-50`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear filters
            </button>
          )}
        </div>
      </div>

      {isFiltered && (
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 bg-white px-5 py-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Active filters:</span>
          {appliedFromDate && <FilterChip label={`From: ${appliedFromDate}`} onClear={() => { setAppliedFromDate(''); setInputFromDate(''); setPage(1) }} />}
          {appliedToDate && <FilterChip label={`To: ${appliedToDate}`} onClear={() => { setAppliedToDate(''); setInputToDate(''); setPage(1) }} />}
          {appliedCampaign && <FilterChip label={appliedCampaign} onClear={() => { setAppliedCampaign(''); setInputCampaign(''); setPage(1) }} />}
          {appliedActivityType && (
            <FilterChip
              label={ACTIVITY_TYPES.find((a) => a.value === appliedActivityType)?.label ?? appliedActivityType}
              onClear={() => { setAppliedActivityType(''); setInputActivityType(''); setPage(1) }}
            />
          )}
        </div>
      )}

      <div className="w-full flex-1 overflow-auto px-6 py-6">
        {/* Notifications List */}
        <div className="overflow-hidden rounded-[3px] border border-slate-200 bg-white">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-14">
              <div className="h-7 w-7 animate-spin rounded-[3px] border-2 border-slate-200 border-t-blue-500" />
              <p className="mt-3 text-xs text-slate-400">Loading notifications…</p>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="flex h-14 w-14 items-center justify-center rounded-[3px] bg-slate-50">
                <Bell className="h-7 w-7 text-slate-300" />
              </div>
              <p className="mt-3 text-sm font-semibold text-slate-600">No notifications</p>
              <p className="mt-1 text-xs text-slate-400">
                {isFiltered ? 'Try adjusting your filters' : "You're all caught up"}
              </p>
              {isFiltered && (
                <button
                  type="button"
                  onClick={handleClearFilter}
                  className="mt-3 rounded-[3px] border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-left">
                <colgroup>
                  <col className={scope === 'mine' ? 'w-[22%]' : 'w-[18%]'} />
                  {scope === 'all' && <col className="w-28" />}
                  <col className="w-40" />
                  <col className={scope === 'mine' ? 'w-[24%]' : 'w-[20%]'} />
                  <col className="w-56" />
                  <col className="w-36" />
                  <col className="w-44" />
                  {scope === 'mine' && <col className="w-14" />}
                </colgroup>
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/40 text-xs font-medium text-slate-500">
                    <th className="px-4 py-2.5">Notification</th>
                    {scope === 'all' && <th className="px-4 py-2.5">Recipient</th>}
                    <th className="px-4 py-2.5">Conversation ID</th>
                    <th className="px-4 py-2.5">Details</th>
                    <th className="px-4 py-2.5">Activity</th>
                    <th className="px-4 py-2.5">Campaign</th>
                    <th className="px-4 py-2.5">Date & Time</th>
                    {scope === 'mine' && <th className="px-4 py-2.5" />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {notifications.map((n) => {
                    const { icon: EventIcon, color, bg } = getEventIcon(n.eventKey)
                    return (
                      <tr key={n.id} className={`group transition-colors hover:bg-slate-50/50 ${!n.isRead ? 'bg-blue-50/30' : ''}`}>
                        <td className="px-4 py-3 align-middle">
                          <button
                            type="button"
                            onClick={() => void handleClick(n)}
                            className="flex w-full items-center gap-3 text-left"
                          >
                            <div className={`relative flex h-9 w-9 shrink-0 items-center justify-center rounded-[3px] ${bg}`}>
                              <EventIcon className={`h-5 w-5 ${color}`} />
                              {!n.isRead && (
                                <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full border-2 border-white bg-blue-500" />
                              )}
                            </div>
                            <span className={`block truncate text-sm ${n.isRead ? 'font-medium text-slate-700' : 'font-semibold text-slate-900'}`}>
                              {n.title}
                            </span>
                          </button>
                        </td>
                        {scope === 'all' && (
                          <td className="px-4 py-3 align-middle">
                            <span className="block truncate text-sm text-slate-600">
                              {n.recipientName ?? <span className="text-slate-300">—</span>}
                            </span>
                          </td>
                        )}
                        <td className="px-4 py-3 align-middle">
                          {n.referenceId || n.conversationId ? (
                            <button
                              type="button"
                              onClick={() => void handleClick(n)}
                              className="block truncate text-xs font-mono font-medium text-slate-700 transition-colors hover:text-[#3366FF]"
                              title={n.referenceId ? (n.conversationId ? `${n.referenceId} (${n.conversationId})` : n.referenceId) : (n.conversationId ?? undefined)}
                            >
                              {n.referenceId || n.conversationId}
                            </button>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          {n.subject || n.description ? (
                            <button
                              type="button"
                              onClick={() => void handleClick(n)}
                              className="flex max-w-full items-center text-left"
                              title={n.subject || n.description || undefined}
                            >
                              <span className="truncate rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-200">
                                {n.subject || n.description}
                              </span>
                            </button>
                          ) : (
                            <span className="text-xs text-slate-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${bg} ${color}`}>
                            {getActivityLabel(n.eventKey)}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <span className="block truncate text-sm text-slate-600">
                            {n.campaignName ?? <span className="text-slate-300">—</span>}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle">
                          <span className="text-sm text-slate-500">
                            {formatDateTime(n.createdAt)}
                          </span>
                        </td>
                        {scope === 'mine' && (
                          <td className="px-4 py-3 text-center align-middle">
                            <Tooltip content="Delete notification">
                              <button
                                type="button"
                                onClick={() => void handleDelete(n.id)}
                                aria-label="Delete notification"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-[3px] text-slate-300 opacity-0 transition-colors group-hover:opacity-100 hover:bg-red-50 hover:text-red-500"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </Tooltip>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/40 px-4 py-3">
                <span className="text-xs text-slate-500">
                  Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} notification{total !== 1 ? 's' : ''}
                </span>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                      aria-label="Previous page"
                      className="flex h-8 w-8 items-center justify-center rounded-[3px] text-slate-500 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    {pageSequence.map((token) =>
                      token.kind === 'ellipsis' ? (
                        <span key={`ellipsis-after-${token.afterPage}`} className="px-1.5 text-sm text-slate-400">
                          …
                        </span>
                      ) : (
                        <button
                          key={token.value}
                          type="button"
                          onClick={() => setPage(token.value)}
                          aria-current={token.value === page ? 'page' : undefined}
                          className={`flex h-8 min-w-8 items-center justify-center rounded-[3px] px-2 text-sm font-medium transition-colors ${
                            token.value === page
                              ? 'bg-blue-600 text-white'
                              : 'text-slate-600 hover:bg-white'
                          }`}
                        >
                          {token.value}
                        </button>
                      ),
                    )}

                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      aria-label="Next page"
                      className="flex h-8 w-8 items-center justify-center rounded-[3px] text-slate-500 transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1 rounded-[3px] bg-blue-50 px-2 py-0.5 text-xs font-medium text-[#3366FF]">
      {label}
      <button
        type="button"
        onClick={onClear}
        className="ml-0.5 rounded-[3px] p-0.5 hover:bg-[#DCE7FF]"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}
